import React, { useState, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Footer from '../components/Footer';
import {
  BellAlertIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  UserGroupIcon,
  MegaphoneIcon,
  SparklesIcon,
  HeartIcon,
  AcademicCapIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  GiftIcon,
  CurrencyDollarIcon,
  RocketLaunchIcon,
  HandRaisedIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';

const ForChurches = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'pricing' ? 'pricing' : 'features';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [billingCycle, setBillingCycle] = useState('annual');
  const contentRef = useRef(null);

  // Sync tab state if URL params change (e.g. redirect from /pricing)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'pricing' && activeTab !== 'pricing') {
      setActiveTab('pricing');
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchTab = (tab) => {
    setActiveTab(tab);
    setSearchParams(tab === 'features' ? {} : { tab }, { replace: true });
    if (contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white">

      {/* Hero */}
      <section className="relative bg-dark text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/church-worship.png"
            alt="Church congregation worshipping together"
            className="h-full w-full object-cover opacity-15"
          />
        </div>
        <div className="relative container mx-auto px-6 py-24 md:py-32 text-center max-w-4xl">
          <p className="text-secondary font-semibold text-sm uppercase tracking-widest mb-4">
            For Churches &amp; Ministries
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6 tracking-tight">
            Help Your People Hear,<br />
            <span className="text-secondary">"Well Done."</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            The Great Commission belongs to every believer in your church — not just your staff. Together On Mission gives them the system to actually do it: reminders, Scripture, your content in their hands, and a dashboard so you can see the fruit.
          </p>
          <p className="text-sm text-gray-400 mb-10">
            Individual disciplemakers use the app <strong className="text-white">free</strong>. Churches subscribe to unlock community tools, content distribution, analytics, and team management.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/create-organization"
              className="inline-flex items-center justify-center bg-secondary text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-opacity-90 transition duration-300"
            >
              Sign Up Your Church
              <ArrowRightIcon className="h-5 w-5 ml-2" />
            </Link>
            <button
              onClick={() => switchTab('pricing')}
              className="inline-flex items-center justify-center bg-white/10 border border-white/20 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white/20 transition duration-300"
            >
              View Plans &amp; Pricing
            </button>
          </div>
        </div>
      </section>

      {/* Sticky Tab Bar — top-16 clears the 64px PublicHeader */}
      <div ref={contentRef} className="sticky top-16 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-6">
          <div className="flex justify-center">
            <div className="flex">
              <button
                onClick={() => switchTab('features')}
                className={`px-8 py-4 text-sm font-semibold transition-colors relative ${
                  activeTab === 'features'
                    ? 'text-primary'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Features
                {activeTab === 'features' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
                )}
              </button>
              <button
                onClick={() => switchTab('pricing')}
                className={`px-8 py-4 text-sm font-semibold transition-colors relative flex items-center gap-2 ${
                  activeTab === 'pricing'
                    ? 'text-primary'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Plans &amp; Pricing
                {activeTab !== 'pricing' && (
                  <span className="bg-secondary/10 text-secondary text-xs font-bold px-2 py-0.5 rounded-full">
                    Free to start
                  </span>
                )}
                {activeTab === 'pricing' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* =========== FEATURES TAB =========== */}
      {activeTab === 'features' && (
        <>
          {/* The Problem */}
          <section className="py-20 bg-white">
            <div className="container mx-auto px-6 max-w-6xl">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-dark mb-6">
                Overcome These Discipleship Barriers
              </h2>
              <p className="text-center text-gray-500 mb-14 max-w-2xl mx-auto">
                Discipleship is why we start our ministries — but as the church grows it gets harder to empower your people, and you end up counting on Sunday programs instead of the Acts 2 model church you really want.
              </p>
              {/* Top row: 3 cards */}
              <div className="grid md:grid-cols-3 gap-8 mb-8">
                {problems.slice(0, 3).map((item) => (
                  <div key={item.title} className="relative bg-gray-50 rounded-2xl p-8 border border-gray-100">
                    <div className="text-5xl font-extrabold text-gray-100 absolute top-4 right-6">{item.number}</div>
                    <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-danger/10 mb-5">
                      <item.icon className="h-6 w-6 text-danger" />
                    </div>
                    <h3 className="text-lg font-bold text-dark mb-2">{item.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>
              {/* Bottom row: 2 cards, centered */}
              <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {problems.slice(3).map((item) => (
                  <div key={item.title} className="relative bg-gray-50 rounded-2xl p-8 border border-gray-100">
                    <div className="text-5xl font-extrabold text-gray-100 absolute top-4 right-6">{item.number}</div>
                    <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-danger/10 mb-5">
                      <item.icon className="h-6 w-6 text-danger" />
                    </div>
                    <h3 className="text-lg font-bold text-dark mb-2">{item.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Plan quote — dark section */}
          <section className="py-20 bg-dark text-white">
            <div className="container mx-auto px-6 max-w-3xl text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-8">What is your plan for making disciples? How is that plan going?</h2>
              <blockquote className="text-lg md:text-xl text-gray-300 leading-relaxed italic">
                Your people want to make disciples. They just need a system. Together On Mission gives every mentor in your church personalized reminders, Bible verse suggestions based on each relationship, content tracks they can walk through with their people, and a way to pray faithfully. Each mentor can disciple as many people as they want — there's no limit. As a leader, you get a dashboard showing real-time activity — prayers prayed, messages sent, disciples added, stories of life change. You can push your own content through the app and keep your disciplemakers encouraged with announcements and a community feed. The platform includes your first 5 mentors. It's simple enough for anyone, and it turns willing hearts into consistent disciplemakers.
              </blockquote>
            </div>
          </section>

          {/* What Your Disciplemakers Get */}
          <section className="py-20 bg-gray-50">
            <div className="container mx-auto px-6">
              <div className="text-center mb-14">
                <p className="text-primary font-semibold text-sm uppercase tracking-widest mb-2">For Every Mentor</p>
                <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4">
                  What Your Disciplemakers Get
                </h2>
                <p className="text-gray-500 max-w-2xl mx-auto">
                  Simple tools that make consistent, personalized discipleship possible for anyone — no seminary degree required.
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {mentorFeatures.map((feature) => (
                  <div key={feature.title} className="bg-white rounded-2xl p-7 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
                    <div className="flex items-center justify-center h-12 w-12 rounded-xl bg-primary/10 mb-5">
                      <feature.icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-bold text-dark mb-2">{feature.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* What Leaders Get */}
          <section className="py-20 bg-white">
            <div className="container mx-auto px-6">
              <div className="text-center mb-14">
                <p className="text-secondary font-semibold text-sm uppercase tracking-widest mb-2">For Church Leaders</p>
                <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4">
                  What You Get as a Leader
                </h2>
                <p className="text-gray-500 max-w-2xl mx-auto">
                  Visibility into what's actually happening, tools to equip your people, and a way to push your own content through your disciplemakers.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                {leaderFeatures.map((feature) => (
                  <div key={feature.title} className="flex gap-5 p-6 bg-gray-50 rounded-2xl border border-gray-100">
                    <div className="flex-shrink-0 flex items-start justify-center h-12 w-12 rounded-xl bg-secondary/10">
                      <feature.icon className="h-6 w-6 text-secondary mt-3" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-dark mb-1">{feature.title}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Content Tracks Highlight */}
          <section className="py-20 bg-gray-50">
            <div className="container mx-auto px-6">
              <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
                <div>
                  <p className="text-primary font-semibold text-sm uppercase tracking-widest mb-2">Your Content, Your Voice</p>
                  <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6">
                    Push Your Own Content Through Your Disciplemakers
                  </h2>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    Create content tracks with your church's sermons, Bible studies, videos, and articles. Your disciplemakers subscribe their people to a track, and the app delivers each resource in order — one conversation at a time.
                  </p>
                  <ul className="space-y-4 mb-8">
                    {contentTrackBullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-3">
                        <CheckCircleIcon className="h-6 w-6 text-secondary flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-gray-600 leading-relaxed">
                    For <strong>family discipleship</strong>, create children's tracks with Bible stories, memory verses, and videos. Parents use the <em>Read Together</em> feature to walk through content with their kids — no device needed for the child.
                  </p>
                </div>
                <div className="relative">
                  <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <BookOpenIcon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <div className="font-bold text-dark">Foundations of Faith</div>
                        <div className="text-xs text-gray-400">12 resources</div>
                      </div>
                    </div>
                    <div className="space-y-3 mb-6">
                      {['Who is God?', 'Why the Bible Matters', 'The Gospel Message', 'What is Prayer?'].map((title, i) => (
                        <div key={title} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                          <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${i < 2 ? 'bg-secondary text-white' : 'bg-gray-200 text-gray-500'}`}>
                            {i < 2 ? '✓' : i + 1}
                          </div>
                          <span className={`text-sm ${i < 2 ? 'text-gray-400 line-through' : 'text-dark font-medium'}`}>{title}</span>
                          {i === 2 && <span className="ml-auto text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">Next</span>}
                        </div>
                      ))}
                      <div className="text-center text-xs text-gray-400 pt-1">+ 8 more resources</div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-secondary h-2 rounded-full" style={{ width: '17%' }}></div>
                    </div>
                    <div className="text-xs text-gray-400 mt-2">2 of 12 sent</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Comparison */}
          <section className="py-20 bg-white">
            <div className="container mx-auto px-6 max-w-4xl">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-dark mb-14">
                Why This, Not That
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="py-4 pr-6 text-gray-400 font-medium text-sm uppercase tracking-wider">Other Tools</th>
                      <th className="py-4 pl-6 text-primary font-medium text-sm uppercase tracking-wider">Together On Mission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisons.map((row, i) => (
                      <tr key={i} className="border-b border-gray-100">
                        <td className="py-4 pr-6 text-gray-400 text-sm">{row.other}</td>
                        <td className="py-4 pl-6 text-dark text-sm font-medium">{row.tom}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* Pricing Teaser (on Features tab) */}
          <section className="py-16 bg-gray-50">
            <div className="container mx-auto px-6 max-w-3xl text-center">
              <div className="bg-white rounded-2xl p-10 shadow-sm border border-gray-100">
                <div className="flex items-center justify-center h-14 w-14 rounded-2xl bg-secondary/10 mx-auto mb-5">
                  <CurrencyDollarIcon className="h-7 w-7 text-secondary" />
                </div>
                <h3 className="text-2xl font-bold text-dark mb-3">Free for Individuals. Built for Churches.</h3>
                <p className="text-gray-500 mb-2">
                  Any disciplemaker can use the app for free — add disciples, send verses, get reminders.
                </p>
                <p className="text-gray-500 mb-6">
                  Churches subscribe to unlock the organization dashboard, content tracks, announcements, story feeds, analytics, and team management. <strong className="text-dark">Platform includes your first 5 seats.</strong>
                </p>
                <button
                  onClick={() => switchTab('pricing')}
                  className="inline-flex items-center justify-center bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition duration-300"
                >
                  See Plans &amp; Pricing
                  <ArrowRightIcon className="h-4 w-4 ml-2" />
                </button>
              </div>
            </div>
          </section>
        </>
      )}

      {/* =========== PRICING TAB =========== */}
      {activeTab === 'pricing' && (
        <>
          {/* Free vs Church */}
          <section className="py-20 bg-white">
            <div className="container mx-auto px-6 max-w-5xl">
              <div className="text-center mb-14">
                <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4">
                  Free for Individuals. Powerful for Churches.
                </h2>
                <p className="text-gray-500 max-w-2xl mx-auto">
                  Anyone can use Together On Mission for free. Churches subscribe to unlock the full suite of leadership and community tools.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {/* Free Card */}
                <div className="bg-gray-50 rounded-2xl p-8 border-2 border-gray-200">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-12 w-12 rounded-xl bg-gray-200 flex items-center justify-center">
                      <GiftIcon className="h-6 w-6 text-gray-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-dark">Individual</h3>
                      <p className="text-2xl font-extrabold text-dark">Free</p>
                    </div>
                  </div>
                  <p className="text-gray-500 text-sm mb-6">
                    Perfect for any believer who wants to be intentional about discipling others.
                  </p>
                  <ul className="space-y-3 mb-8">
                    {freeFeatures.map((f) => (
                      <li key={f} className="flex items-start gap-2.5">
                        <CheckCircleIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600 text-sm">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/signup"
                    className="block w-full text-center bg-gray-200 text-dark px-6 py-3 rounded-lg font-semibold hover:bg-gray-300 transition"
                  >
                    Sign Up Free
                  </Link>
                </div>

                {/* Church Card */}
                <div className="bg-white rounded-2xl p-8 border-2 border-primary shadow-lg ring-2 ring-primary/10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <RocketLaunchIcon className="h-6 w-6 text-primary" />
                    </div>
                <div>
                    <h3 className="text-xl font-bold text-dark">Church Organization</h3>
                    <p className="text-sm text-gray-500">First 5 seats included with all platforms</p>
                  </div>
                  </div>
                  <p className="text-gray-500 text-sm mb-6">
                    Everything in Free, plus community management, analytics, content distribution, and team tools.
                  </p>
                  <ul className="space-y-3 mb-8">
                    {churchFeatures.map((f) => (
                      <li key={f} className="flex items-start gap-2.5">
                        <CheckCircleIcon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/create-organization"
                    className="block w-full text-center bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition"
                  >
                    Sign Up Your Church
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Find Your Fit — platform pricing (main purchase area) */}
          <section className="py-20 bg-gray-50">
            <div className="container mx-auto px-6 max-w-5xl">
              <div className="text-center mb-6">
                <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4">Choose Your Platform</h2>
                <p className="text-gray-500 max-w-2xl mx-auto">
                  The platform is the platform. <strong className="text-dark">First 5 seats included</strong> — add seat packs as you grow. We help starter churches.
                </p>
              </div>

              {/* Monthly / Annual Toggle */}
              <div className="flex justify-center mb-4">
                <div className="inline-flex items-center bg-gray-100 rounded-full p-1">
                  <button
                    onClick={() => setBillingCycle('monthly')}
                    className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                      billingCycle === 'monthly'
                        ? 'bg-white text-dark shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setBillingCycle('annual')}
                    className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                      billingCycle === 'annual'
                        ? 'bg-white text-dark shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Annual
                  </button>
                </div>
              </div>
              {billingCycle === 'annual' && (
                <p className="text-center text-secondary text-sm font-semibold mb-10">
                  Save up to 20% with annual billing
                </p>
              )}
              {billingCycle === 'monthly' && (
                <p className="text-center text-gray-400 text-sm mb-10">
                  Switch to annual to save up to 20%
                </p>
              )}

              {/* Info Pills */}
              <div className="flex flex-wrap justify-center gap-3 mb-10">
                <div className="inline-flex items-center gap-2 bg-secondary/10 text-secondary px-4 py-2 rounded-full text-sm font-semibold">
                  <CheckCircleIcon className="h-5 w-5" />
                  First 5 seats included with all platforms
                </div>
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-semibold">
                  <CheckCircleIcon className="h-5 w-5" />
                  Unlimited disciples per mentor
                </div>
                <div className="inline-flex items-center gap-2 bg-gray-100 text-gray-600 px-4 py-2 rounded-full text-sm font-semibold">
                  <CheckCircleIcon className="h-5 w-5" />
                  Admins don&apos;t count as seats
                </div>
              </div>

              {/* Platform Cards — Starter and Standard */}
              <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto mb-14">
                {platformTiers.map((tier) => {
                  const price = billingCycle === 'annual' ? tier.priceAnnual : tier.priceMonthly;
                  const savings = tier.savings || 0;
                  return (
                    <div key={tier.id} className={`bg-white rounded-2xl p-7 border text-center flex flex-col ${tier.highlight ? 'border-primary shadow-lg ring-2 ring-primary/20' : 'border-gray-200'}`}>
                      {tier.badge && <div className="text-xs font-bold text-primary uppercase tracking-wider mb-2">{tier.badge}</div>}
                      <h3 className="text-lg font-bold text-dark mb-1">{tier.name}</h3>
                      <p className="text-primary font-semibold text-sm mb-4">{tier.range}</p>
                      <div className="mb-4">
                        <span className="text-4xl font-extrabold text-dark">${price}</span>
                        <span className="text-gray-400 text-sm">{billingCycle === 'annual' ? '/yr' : '/mo'}</span>
                      </div>
                      {billingCycle === 'annual' && savings > 0 && (
                        <p className="text-xs text-secondary font-medium mb-3">
                          saves ${savings}/yr
                        </p>
                      )}
                      <p className="text-sm text-gray-500 mb-5 flex-grow">{tier.description}</p>
                      <Link
                        to={`/create-organization?tier=${tier.id}`}
                        className={`block w-full text-center px-4 py-2.5 rounded-lg text-sm font-semibold transition ${
                          tier.highlight
                            ? 'bg-primary text-white hover:bg-opacity-90'
                            : 'bg-gray-100 text-dark hover:bg-gray-200'
                        }`}
                      >
                        {tier.cta}
                      </Link>
                      <p className="text-xs text-gray-400 mt-3">Unlimited disciples per mentor</p>
                    </div>
                  );
                })}
              </div>

              {/* Seat packs — de-emphasized */}
              <div className="mt-14 pt-10 border-t border-gray-200">
                <p className="text-center text-sm text-gray-500 mb-6">
                  Add seat packs as you grow. ~$4–5 per disciplemaker, volume discount at higher tiers.
                </p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
                  {seatPacks.map((pack) => (
                    <div key={pack.name} className="bg-gray-50 rounded-xl p-4 border border-gray-100 text-center">
                      <div className="font-semibold text-dark">{pack.name}</div>
                      <div className="text-sm text-primary font-medium">{pack.seats} disciplemakers</div>
                      <div className="text-lg font-bold text-dark mt-2">
                        ${billingCycle === 'annual' ? pack.priceAnnual : pack.priceMonthly}
                        <span className="text-gray-400 text-sm font-normal">/{billingCycle === 'annual' ? 'yr' : 'mo'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Getting Started */}
          <section className="py-20 bg-gray-50">
            <div className="container mx-auto px-6 max-w-3xl">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-dark mb-14">
                Get Started in 5 Minutes
              </h2>
              <div className="space-y-8">
                {gettingStartedSteps.map((step, i) => (
                  <div key={step.title} className="flex gap-6 items-start">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary text-white flex items-center justify-center font-bold text-lg">
                      {i + 1}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-dark mb-1">{step.title}</h3>
                      <p className="text-gray-500 text-sm">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Questions */}
          <section className="py-16 bg-white">
            <div className="container mx-auto px-6 max-w-3xl text-center">
              <h2 className="text-2xl font-bold text-dark mb-4">Questions About Pricing?</h2>
              <p className="text-gray-500 mb-6">
                Our team is here to help you find the right plan. Early adopter pricing is available for churches that join now.
              </p>
              <a
                href="mailto:hello@togetheronmission.org"
                className="inline-flex items-center text-primary font-semibold hover:underline"
              >
                hello@togetheronmission.org
                <ArrowRightIcon className="h-4 w-4 ml-1" />
              </a>
            </div>
          </section>
        </>
      )}

      {/* =========== SHARED FOOTER CTA =========== */}
      <section className="py-20 bg-secondary text-white">
        <div className="container mx-auto px-6 text-center max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Discipleship Doesn't Happen by Accident.
          </h2>
          <p className="text-xl text-white/80 mb-10">
            Give your people the system that turns "I want to disciple someone" into "I discipled someone this week."
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/create-organization"
              className="inline-flex items-center justify-center bg-white text-secondary px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition duration-300"
            >
              Sign Up Your Church
              <ArrowRightIcon className="h-5 w-5 ml-2" />
            </Link>
            <Link
              to="/landing"
              className="inline-flex items-center justify-center bg-white/10 border border-white/30 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white/20 transition duration-300"
            >
              Learn More
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

/* --- Data --- */

const problems = [
  { number: '1', title: 'People Forget to Follow Up', description: 'Life gets busy. A week becomes a month. The relationship fades. Without a system, consistency is the first casualty.', icon: ClockIcon },
  { number: '2', title: "People Don't Know What to Say", description: "They want to share something meaningful but don't know where to start. So they don't start at all.", icon: ChatBubbleLeftRightIcon },
  { number: '3', title: "They Think It's the Pastor's Job", description: "Many believers assume discipleship is for church leaders. But Jesus gave the command to all believers (Matthew 28:19). The church exists to equip the saints to do the work of ministry (Ephesians 4:11\u201312) — not to do it for them.", icon: HandRaisedIcon },
  { number: '4', title: "They've Never Been Discipled Themselves", description: "If no one ever walked with them, they assume they can't do it either. TOM lets you learn as you go — the app guides you step by step so you don't need to have experienced it to start doing it.", icon: LightBulbIcon },
  { number: '5', title: "Leaders Can't See What's Happening", description: "There's no visibility into whether discipleship is actually taking place. You're guessing, not leading.", icon: ChartBarIcon },
];

const mentorFeatures = [
  { title: 'Personalized Reminders', description: 'Every disciple has their own reminder cycle — 3 days, a week, two weeks. Push notifications keep your people consistent.', icon: BellAlertIcon },
  { title: 'Smart Verse Suggestions', description: "The app suggests Bible verses based on the mentor's notes, the disciple's spiritual stage, and what's already been sent. No repeats.", icon: SparklesIcon },
  { title: 'Content Tracks', description: 'Walk a disciple through a curated series — videos, articles, audio — sent one at a time. Progress is tracked automatically.', icon: BookOpenIcon },
  { title: 'Prayer Guidance', description: 'Daily prayer suggestions based on where each disciple is spiritually. One tap to pray, one tap to get a new suggestion.', icon: HeartIcon },
  { title: 'Notes & Observations', description: 'Quick notes after every conversation. They feed back into the verse algorithm, making each message more personal over time.', icon: AcademicCapIcon },
  { title: 'Read Together', description: "Parents mentoring kids can walk through Bible stories and memory verses on-screen together. No separate device needed for the child.", icon: DevicePhoneMobileIcon },
];

const leaderFeatures = [
  { title: 'Real-Time Dashboard', description: 'See activity as it happens: prayers prayed, messages sent, disciples added, new members joined. Filter by date range — this month, last 30 days, all time.', icon: ChartBarIcon },
  { title: 'Custom Content Tracks', description: "Create tracks with your church's sermons, studies, and resources. Your disciplemakers subscribe their people and the app handles delivery order and progress tracking.", icon: BookOpenIcon },
  { title: 'Announcements', description: 'Post encouragement, training reminders, prayer requests, or mission updates. Every disciplemaker in your org sees them in their community feed.', icon: MegaphoneIcon },
  { title: 'Community Story Feed', description: 'Disciplemakers share what God is doing — spiritual conversations, moments of encouragement, people coming to faith. You moderate and celebrate.', icon: ChatBubbleLeftRightIcon },
  { title: 'Invitation & Onboarding', description: 'Invite by email or share an org code at a training event. New members get a custom welcome message (and optional video) when they join.', icon: UserGroupIcon },
  { title: 'Seat Management', description: "The platform includes 5 disciplemaker seats. Add seat packs as you grow. Assign manually or turn on auto-allocation. Admins don't count against your seats.", icon: ShieldCheckIcon },
];

const contentTrackBullets = [
  'Sermon series, Bible studies, articles, videos — any format',
  'Resources delivered in order, one at a time',
  'Progress tracked per disciple automatically',
  "Children's tracks with Bible stories, memory verses, and video",
];

const comparisons = [
  { other: 'Generic CRM or follow-up app', tom: 'Built specifically for spiritual mentoring' },
  { other: 'User creates all content from scratch', tom: 'Smart verse suggestions and curated content tracks' },
  { other: 'No reminders or basic calendar alerts', tom: 'Per-relationship reminder cycles with push notifications' },
  { other: 'No visibility for church leaders', tom: 'Organization dashboard with real-time activity and stats' },
  { other: 'Desktop-first or web-only', tom: 'Mobile-first, designed for the pocket' },
  { other: 'One-size-fits-all messaging', tom: "Adapts to each disciple's spiritual stage and context" },
  { other: "No children's discipleship support", tom: 'Read Together feature for family discipleship' },
  { other: 'No community features', tom: 'Private story feed, announcements, shared encouragement' },
];

const freeFeatures = [
  'Unlimited disciples',
  'Smart verse suggestions',
  'Send messages via SMS & email',
  'Personalized reminder cycles',
  'Prayer guidance per disciple',
  'Notes & observations',
  'Faith journey tracking',
  'Read Together for kids',
];

const churchFeatures = [
  'Everything in Free for every member',
  '5 disciplemaker seats included',
  'Organization dashboard & analytics',
  'Custom content track creation',
  'Announcements to your team',
  'Private community story feed',
  'Email invitations & org codes',
  'Seat management & auto-allocation',
  'Welcome message & onboarding video',
  'Great Commission impact stats',
];

/* Platform tiers — Starter and Standard (choose on signup step) */
const platformTiers = [
  { id: 'starter', name: 'Starter', range: 'Pilot / Getting started', priceMonthly: 49, priceAnnual: 490, description: 'We help starter churches. Full platform with 5 seats included. Add seat packs as you grow.', highlight: false, badge: 'We help starter churches', cta: 'Get Started', savings: 98 },
  { id: 'standard', name: 'Standard', range: 'Established churches', priceMonthly: 99, priceAnnual: 990, description: 'The standard platform price. Full access, 5 seats included. Add seat packs as your movement grows.', highlight: true, badge: 'Standard', cta: 'Get Started', savings: 198 },
];

/* Seat packs — de-emphasized, first 5 included with all platforms */
const seatPacks = [
  { name: 'Starter Pack', seats: 15, priceMonthly: 75, priceAnnual: 750 },
  { name: 'Growth Pack', seats: 50, priceMonthly: 250, priceAnnual: 2500 },
  { name: 'Active Pack', seats: 100, priceMonthly: 450, priceAnnual: 4500 },
  { name: 'City Pack', seats: 250, priceMonthly: 1000, priceAnnual: 10000 },
];

const churchTiers = [
  { name: 'Starter Church', range: '0–100 people', description: 'Ideal for forming or smaller churches that want to pilot an intentional discipleship pathway without adding complexity.', detail: 'Establish rhythm, language, and shared practice as the foundation of a growing discipleship culture.' },
  { name: 'Standard Church', range: '101–300 people', description: 'Built for established churches that need consistency across groups, leaders, and ministries.', detail: 'Gain clarity, alignment, and visibility as discipleship moves from informal to intentional.' },
  { name: 'Growing Church', range: '301–750 people', description: 'Designed for churches experiencing increased complexity and leadership layers.', detail: 'Structure, shared metrics, and coordination to keep discipleship aligned as the church grows.' },
  { name: 'Multisite / Network', range: '750+ or multiple campuses', description: 'Created for churches managing discipleship across campuses, services, or locations.', detail: 'Advanced coordination and support for scaling a unified discipleship culture across distance and teams.' },
];

const gettingStartedSteps = [
  { title: 'Sign up your organization', description: "Create your church's account on the web app. It takes less than a minute." },
  { title: 'Start with the platform', description: "Your first 5 disciplemakers are included. Add a seat pack when you're ready to grow." },
  { title: 'Invite your people', description: 'Send email invitations or share your org code at a training event. Everyone joins in seconds.' },
  { title: 'Create or assign content tracks', description: "Build your own tracks or use existing ones. Subscribe disciplemakers and they're off." },
  { title: 'Watch the dashboard', description: 'See prayers, messages, connections, and stories of life change — in real time.' },
];

export default ForChurches;
